<template>
  <div v-if="loading" class="spinner-wrapper">
    <Spinner />
  </div>

  <div v-if="error">
    <Alert
      :message="error"
      alertType="alert-danger"
      :alertIcon="alertIcon"
      :closable="alertClosable"
      @onClose="dismissAlert"
      :isSelfService="styleForSelfService"
    />
  </div>

  <div v-if="isAllContentLoaded">
    <!-- Privacy Policy Content -->
    <div class="col-md-12 mt-4 more-options">
      <div
        class="d-flex justify-content-start more-options-header align-items-center"
        @click="togglePrivacyContent"
      >
        <div class="more-option-text" :class="{ 'fs-2': styleForSelfService }">
          {{ t("PAYJOY_PRIVACY_POLICY") }}
        </div>
        <div class="">
          <i class="fa arrow-icon" :class="displayPrivacyIconClass"></i>
        </div>
      </div>
      <div class="more-options-body" v-if="displayPrivacyContent">
        <div
          class="content p-2 mt-2"
          :class="{ 'fs-3': styleForSelfService }"
          v-html="privacyPolicyContent"
        ></div>
      </div>
    </div>

    <!-- Terms & Conditions Content -->
    <div class="col-md-12 more-options">
      <div
        class="d-flex justify-content-start more-options-header align-items-center"
        @click="toggleTermsContent"
      >
        <div class="more-option-text" :class="{ 'fs-2': styleForSelfService }">
          {{ t("TERMS_AND_CONDITIONS") }}
        </div>
        <div class="">
          <i class="fa arrow-icon" :class="displayTermsContentIconClass"></i>
        </div>
      </div>
      <div class="more-options-body" v-if="displayTermsContent">
        <div
          class="content p-2 mt-2"
          :class="{ 'fs-3': styleForSelfService }"
          v-html="termsContent"
        ></div>
      </div>
    </div>

    <!-- Terms & Privacy Explicit Acceptance -->
    <div v-if="requiresManualAcknowledgement">
      <!-- Privacy Policy Input -->
      <div
        class="d-flex justify-content-start more-options-header align-items-center"
      >
        <input
          type="checkbox"
          class="form-check-input fs-4 m-1 ps-0"
          name="ckAcknowledgePrivacyPolicy"
          id="ckAcknowledgePrivacyPolicy"
          v-model="isPrivacyPolicyAccepted"
        />
        <label class="more-option-text" for="ckAcknowledgePrivacyPolicy">{{
          t("ACKNOWLEDGE_PRIVACY_POLICY")
        }}</label>
      </div>

      <!-- Terms & Conditions Input -->
      <div
        class="d-flex justify-content-start more-options-header align-items-center"
      >
        <input
          type="checkbox"
          class="form-check-input fs-4 m-1 ps-0"
          name="ckAcknowledgeTermsAndConditions"
          id="ckAcknowledgeTermsAndConditions"
          v-model="isTermsAndConditionsAccepted"
        />
        <label for="ckAcknowledgeTermsAndConditions">{{
          t("ACKNOWLEDGE_TERMS_AND_CONDITIONS")
        }}</label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import logAction from "@/apps/merchant3/services/action-logs.service";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import { useAlert } from "@/apps/merchant3/hooks/useAlert";
import { useTermsAndPrivacyContent } from "@/apps/merchant3/hooks/useTermsAndPrivacyContent";
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import Spinner from "@/apps/merchant3/shared-components/ui/Spinner.vue";

const props = defineProps<{
  locale: string;
  cartTag?: string;
  requiresManualAcknowledgement: boolean;
  styleForSelfService?: boolean;
}>();

const emit = defineEmits<{
  (event: "privacy-acceptance-changed", value: boolean): void;
  (event: "terms-acceptance-changed", value: boolean): void;
}>();

const { t } = useI18n();

// -----------------------------------------------------------------------------
// Alert state
// -----------------------------------------------------------------------------
const { alertIcon, alertClosable, dismissAlert } = useAlert();

const {
  loading,
  error,
  privacyPolicyContent,
  termsContent,
  isAllContentLoaded,
} = useTermsAndPrivacyContent(props.locale, props.cartTag);

// -----------------------------------------------------------------------------
// Provacy policy state
// -----------------------------------------------------------------------------
const displayPrivacyContent = ref(false);
const displayPrivacyIconClass = ref("fa-angle-right");
const isPrivacyPolicyAccepted = ref(false);

watch(isPrivacyPolicyAccepted, (newValue) => {
  logAction(ActionLogs.Events.PRIVACY_POLICY_CHECKBOX_CHECKED, {
    checked: newValue,
  });

  emit("privacy-acceptance-changed", newValue);
});

const togglePrivacyContent = () => {
  if (displayTermsContent.value && !displayPrivacyContent.value) {
    displayTermsContent.value = false;
    displayTermsContentIconClass.value = "fa-angle-right";
  }

  displayPrivacyContent.value = !displayPrivacyContent.value;
  logEvent(EventLogs.Events.PRIVACY_POLICY_DROPDOWN_CLICKED);
  logAction(ActionLogs.Events.PP_LOADED_BY_DROPDOWN_CLICK);

  if (displayPrivacyContent.value) {
    displayPrivacyIconClass.value = "fa-angle-down";
  } else {
    displayPrivacyIconClass.value = "fa-angle-right";
  }

  if (!isPrivacyPolicyAccepted.value) {
    isPrivacyPolicyAccepted.value = true;
  }
};

// -----------------------------------------------------------------------------
// Terms and conditions state
// -----------------------------------------------------------------------------
const displayTermsContent = ref(false);
const displayTermsContentIconClass = ref("fa-angle-right");
const isTermsAndConditionsAccepted = ref(false);

watch(isTermsAndConditionsAccepted, (newValue) => {
  logAction(ActionLogs.Events.TERMS_AND_CONDITIONS_CHECKBOX_CHECKED, {
    checked: newValue,
  });

  emit("terms-acceptance-changed", newValue);
});

const toggleTermsContent = () => {
  if (displayPrivacyContent.value && !displayTermsContent.value) {
    displayPrivacyContent.value = false;
    displayPrivacyIconClass.value = "fa-angle-right";
  }

  displayTermsContent.value = !displayTermsContent.value;
  logEvent(EventLogs.Events.TERMS_AND_CONDITIONS_DROPDOWN_CLICKED);
  logAction(ActionLogs.Events.TC_LOADED_BY_DROPDOWN_CLICK);

  if (displayTermsContent.value) {
    displayTermsContentIconClass.value = "fa-angle-down";
  } else {
    displayTermsContentIconClass.value = "fa-angle-right";
  }

  if (!isTermsAndConditionsAccepted.value) {
    isTermsAndConditionsAccepted.value = true;
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.content {
  border: 1px solid $pj-color-green;
  height: 50vh;
  overflow-y: auto;
  text-align: left;
  border-radius: 8px;
  font-size: 14px;
}
.more-options {
  font-size: 18px;
  line-height: 1.5;
  vertical-align: text-top;
}
.more-options-header {
  cursor: pointer;
  color: $pj-color-zomp;
  font-size: 1rem;
}
.arrow-icon {
  padding: 10px;
  font-size: 30px;
}
</style>
